import React, { useContext, useEffect, useState } from "react";

import {
  AccountCircle,
  Apps as AppsIcon,
  ExitToApp,
  Explore,
  Menu as MenuIcon,
  Refresh as RefreshIcon,
  VpnKey,
} from "@mui/icons-material";
import {
  AppBar,
  Autocomplete,
  Avatar,
  Box,
  Button,
  createFilterOptions,
  Divider,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { colors } from "@rubi/ui";
import { useCookies } from "react-cookie";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { devEnvironment } from "@/common/featureFlags";
import LanguageSelector, {
  SUPPORTED_LANGUAGES,
} from "@/common/LanguageSelector";
import {
  IPoliceServiceConfiguration,
  RUBICON_ADMIN_ORG_IDS,
} from "@/common/serviceConfigurations";
import PartnerOrgSwitcher from "@/components/partner/PartnerOrgSwitcher";
import { parseUserPermissions } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";
import { useAutoRefresh } from "@/contexts/AutoRefreshContext";
import { ColorModeToggle } from "@/contexts/ColorModeContext";
import { useDevEnvironment } from "@/contexts/DevContext";
import { useServiceConfigurations } from "@/hooks/useCommunities";
import { IUserPermission } from "@/types/typesAlerts";

import AutoRefreshIndicator from "./AutoRefreshIndicator";

interface CustomAppBarProps {
  onMenuClick?: () => void;
}

export const APP_BAR_HEIGHT = 48;

// The org switcher's surface. The portal AppBar is a permanently dark surface
// (`frame.bg`) in both color modes, so this doesn't invert with the theme. It
// has no slot in the shell's `frame` palette, so it stays local at its
// pre-swap hex.
const APP_BAR_INPUT_BG = "#23243a"; // org switcher input + its dropdown panel

// Match on full name as well as short name, so "TPS" finds "Toronto Police Service".
const orgFilterOptions = createFilterOptions<IPoliceServiceConfiguration>({
  stringify: (cfg) => `${cfg.fullName ?? ""} ${cfg.shortName ?? ""}`,
});

const CustomAppBar: React.FC<CustomAppBarProps> = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const serviceConfigurations = useServiceConfigurations();
  const location = useLocation();
  const { i18n } = useTranslation();
  const { isDevEnv, toggleDevEnv } = useDevEnvironment();
  const { currentUser, getOrgIDFromURL, logout } = useContext(AuthStateContext);
  // Views that reload their own data register a handler here; when one is
  // active the refresh control reloads just that data instead of the page.
  const {
    isActive: canRefreshInPlace,
    isRefreshing,
    refreshNow,
  } = useAutoRefresh();

  const [currentOrg, setCurrentOrg] = useState(getOrgIDFromURL());
  const [cookies, setCookies] = useCookies();
  const languageOptions = SUPPORTED_LANGUAGES.filter((language) =>
    ["en", "fr"].includes(language.code),
  );
  const currentLanguage = cookies.language ?? "en";
  const [showDevUserMenuItem, setShowDevUserMenuItem] = useState(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (currentLanguage !== i18n.language) {
      i18n.changeLanguage(currentLanguage);
    }
  }, [currentLanguage, i18n]);

  useEffect(() => {
    setCurrentOrg(getOrgIDFromURL());
  }, [location.pathname, getOrgIDFromURL]);

  const setCurrentLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    setCookies("language", lang);
    window.location.reload();
  };

  const onServiceChange = (serviceID: string) => {
    setCurrentOrg(serviceID);
    const pathParts = window.location.pathname.split("/").filter(Boolean);
    const appName = pathParts[0];
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    let newPathParts: string[];
    if (pathParts.length > 1 && uuidRegex.test(pathParts[1])) {
      newPathParts = [appName, serviceID, ...pathParts.slice(2)];
    } else {
      newPathParts = [appName, serviceID, ...pathParts.slice(1)];
    }

    const policeService = Object.values(serviceConfigurations).find(
      (cfg) => cfg.id === serviceID || cfg.testID === serviceID,
    );
    if (policeService && policeService.apiRegion) {
      sessionStorage.setItem("apiRegion", policeService.apiRegion);
    } else {
      sessionStorage.removeItem("apiRegion");
    }

    const newURL = `/${newPathParts.join("/")}`;
    window.location.pathname = newURL;
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleUserMenuClose = () => setAnchorEl(null);

  // reveal Dev item after 5 clicks on the user header …
  const [clicks, setClicks] = useState<number>(0);
  const handleUserNameClick = () => {
    const newCount = clicks + 1;
    setClicks(newCount);
    if (newCount >= 5) setShowDevUserMenuItem(true);
  };

  const orgName =
    Object.values(serviceConfigurations).find(
      (cfg) => cfg.id === currentOrg || cfg.testID === currentOrg,
    )?.fullName || "";

  const getOrgOptionId = (cfg: IPoliceServiceConfiguration) =>
    devEnvironment() ? cfg.testID : cfg.id;
  const orgOptionsById = new Map<string, IPoliceServiceConfiguration>();
  for (const cfg of Object.values(serviceConfigurations)) {
    const optionId = getOrgOptionId(cfg);
    if (optionId) orgOptionsById.set(optionId, cfg);
  }
  const orgOptions = [...orgOptionsById.values()].sort(
    (a, b) => (a?.fullName ?? "").localeCompare(b?.fullName ?? "") ?? 0,
  );
  const validOrgIds = orgOptions.map(getOrgOptionId);
  const isPartnerApp = location.pathname.startsWith("/rubipartner");
  const canViewPartnerAsOrg = currentUser
    ? parseUserPermissions(currentUser.permissions as IUserPermission[])
        .canViewPartnerAsOrg
    : false;

  const isValidOrgId = currentOrg && validOrgIds.includes(currentOrg);
  const selectedOrgOption = orgOptions.find(
    (cfg) => cfg.id === currentOrg || cfg.testID === currentOrg,
  );

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: "frame.bg",
        // AppBar extends Paper, which adds a white elevation overlay in dark
        // mode on top of backgroundColor. Suppress it so the bar stays exactly
        // frame.bg and matches the sidebar, which paints the same token
        // with no overlay. The elevation={1} shadow is unaffected.
        backgroundImage: "none",
        color: "frame.text.primary",
        ".MuiToolbar-root": {
          height: APP_BAR_HEIGHT,
          minHeight: APP_BAR_HEIGHT,
        },
      }}
      elevation={1}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="toggle sidebar"
          edge="start"
          onClick={onMenuClick}
          sx={{ mr: { xs: 1, md: 2 } }}
        >
          <MenuIcon />
        </IconButton>

        <Button
          id="appsButton"
          disabled={currentUser?.profile_update_required ?? false}
          variant="outlined"
          onClick={() => navigate("/")}
          // MUI's own outlined colours, drawn from frame.appsButton instead of
          // a palette channel: the variables MUI's outlined Button reads.
          sx={(theme) => {
            const ink = theme.palette.frame.appsButton;
            return {
              "--variant-outlinedColor": ink,
              "--variant-outlinedBorder": theme.alpha(ink, 0.5),
              "&:hover": {
                "--variant-outlinedBorder": ink,
                "--variant-outlinedBg": theme.alpha(
                  ink,
                  theme.palette.action.hoverOpacity,
                ),
              },
              px: 1,
              mr: { xs: 1, md: 2 },
              textTransform: "uppercase",
            };
          }}
        >
          <AppsIcon sx={{ mr: { xs: 0, md: 1 } }} />
          <Typography sx={{ fontWeight: "bold" }} className="topAppsText">
            APPS
          </Typography>
        </Button>

        {devEnvironment() && (
          <FormControlLabel
            control={
              <Switch
                checked={isDevEnv}
                onChange={toggleDevEnv}
                // Primary blue thumb and track in both modes (designer's
                // shell pass); the track used to be a leftover coral red.
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "primary.main",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "primary.dark",
                  },
                }}
              />
            }
            label="Dev Mode"
            sx={{ pl: { xs: 0.5, md: 2 }, color: "frame.text.primary" }}
            slotProps={{
              typography: { fontSize: "0.85rem" },
            }}
          />
        )}

        {/* Org Switcher */}
        {isValidOrgId &&
          selectedOrgOption &&
          currentUser?.rubi_org_id &&
          RUBICON_ADMIN_ORG_IDS.includes(currentUser.rubi_org_id) && (
            <Box
              sx={{
                ml: { xs: 1, md: 2 },
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  mr: { xs: 0, md: 1 },
                  fontSize: { xs: "0.8rem", md: "1rem" },
                }}
              >
                Viewing as:
              </Typography>
              <Autocomplete
                options={orgOptions}
                value={selectedOrgOption}
                onChange={(event, cfg) => {
                  if (cfg) {
                    onServiceChange(getOrgOptionId(cfg));
                  }
                }}
                getOptionLabel={(cfg) => cfg.fullName ?? ""}
                getOptionKey={getOrgOptionId}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={orgFilterOptions}
                disableClearable
                openOnFocus
                selectOnFocus
                handleHomeEndKeys
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search organizations"
                    slotProps={{
                      htmlInput: {
                        ...params.inputProps,
                        "aria-label": "Viewing as",
                      },
                    }}
                  />
                )}
                sx={(theme) => ({
                  ml: 0.5,
                  pb: 0.5,
                  width: { xs: "70%", md: 200 },
                  "& .MuiInputBase-root": {
                    height: "35px",
                    color: colors.common.white,
                    background: APP_BAR_INPUT_BG,
                  },
                  "& .MuiSvgIcon-root": { color: colors.common.white },
                  // Dark mode: outlined only, on the shell surface.
                  ...theme.applyStyles("dark", {
                    "& .MuiInputBase-root": { background: "transparent" },
                  }),
                })}
                slotProps={{
                  paper: {
                    sx: (theme) => ({
                      background: APP_BAR_INPUT_BG,
                      color: colors.common.white,
                      "& .MuiAutocomplete-noOptions": {
                        color: colors.common.white,
                      },
                      // Dark mode: the shell surface, outlined like the user menu.
                      ...theme.applyStyles("dark", {
                        background: theme.palette.frame.bg,
                        border: `1px solid ${theme.palette.divider}`,
                      }),
                    }),
                  },
                  listbox: {
                    sx: (theme) => ({
                      maxHeight: 400,
                      scrollbarColor: `${colors.common.white} ${APP_BAR_INPUT_BG}`,
                      scrollbarWidth: "thin",
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: colors.common.white,
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: APP_BAR_INPUT_BG,
                      },
                      "&& .MuiAutocomplete-option": {
                        color: colors.common.white,
                        '&[aria-selected="true"]': {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.32,
                          ),
                        },
                        "&.Mui-focused, &.Mui-focusVisible": {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.48,
                          ),
                        },
                      },
                      // Dark mode: neutral highlights matching the sidebar's
                      // active item instead of the blue tint.
                      ...theme.applyStyles("dark", {
                        scrollbarColor: `${colors.common.white} ${theme.palette.frame.bg}`,
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: theme.palette.frame.bg,
                        },
                        // Replaces the option block above wholesale (shallow
                        // merge), so it repeats the text colour.
                        "&& .MuiAutocomplete-option": {
                          color: colors.common.white,
                          '&[aria-selected="true"]': {
                            backgroundColor: "var(--shell-active-bg)",
                          },
                          "&.Mui-focused, &.Mui-focusVisible": {
                            backgroundColor: theme.palette.action.selected,
                          },
                        },
                      }),
                    }),
                  },
                }}
              />
            </Box>
          )}

        {isPartnerApp && canViewPartnerAsOrg && (
          <PartnerOrgSwitcher value={currentOrg ?? null} />
        )}

        <Box sx={{ flexGrow: 1 }} />

        {/* Language Selector */}
        {!location.pathname.includes("rubireportv2") && (
          <LanguageSelector
            value={currentLanguage}
            onChange={setCurrentLanguage}
            languages={languageOptions}
            selectedDisplayMode="code"
            triggerMinWidth={72}
          />
        )}

        <ColorModeToggle />

        {/* Auto-refresh status */}
        <AutoRefreshIndicator />

        {/* Refresh Button */}
        <Tooltip title={canRefreshInPlace ? "Refresh now" : "Refresh"}>
          {/* span keeps the tooltip working while the button is disabled */}
          <span style={{ display: "flex" }}>
            <IconButton
              color="inherit"
              onClick={() =>
                canRefreshInPlace ? refreshNow() : window.location.reload()
              }
              disabled={canRefreshInPlace && isRefreshing}
              sx={{ ml: { xs: 0.5, md: 1 } }}
            >
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>

        {/* User Menu */}
        <Box sx={{ ml: { xs: 0, md: 1 } }}>
          <Tooltip title="Account">
            <IconButton onClick={handleUserMenuOpen} color="inherit">
              <Avatar sx={{ bgcolor: "transparent", color: "inherit" }}>
                <AccountCircle />
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleUserMenuClose}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            PaperProps={{
              elevation: 0,
              sx: {
                mt: 1.5,
                minWidth: 220,
                border: 1,
                borderColor: "divider",
                "& .MuiAvatar-root": {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
              },
            }}
          >
            <Box
              onClick={handleUserNameClick}
              sx={{
                p: 2,
                textAlign: "center",
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              <AccountCircle
                sx={{ width: 48, height: 48, color: "action.active", mb: 1 }}
              />
              <Typography variant="subtitle1">
                {`${currentUser?.first_name ?? ""} ${
                  currentUser?.last_name ?? ""
                }`}
              </Typography>
              <Typography variant="body2" noWrap>
                {currentUser?.email ?? ""}
              </Typography>
              <Typography variant="body2" noWrap>
                {orgName}
              </Typography>
            </Box>
            <Divider />
            <MenuItem
              onClick={() => {
                handleUserMenuClose();
                navigate(`/users/${currentUser?.id}/profile`);
              }}
            >
              <ListItemIcon>
                <AccountCircle fontSize="small" color="action" />
              </ListItemIcon>
              <ListItemText>Profile</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleUserMenuClose();
                navigate("/changepassword");
              }}
            >
              <ListItemIcon>
                <VpnKey fontSize="small" color="action" />
              </ListItemIcon>
              <ListItemText>Change Password</ListItemText>
            </MenuItem>
            {showDevUserMenuItem && (
              <MenuItem
                onClick={() => {
                  handleUserMenuClose();
                  navigate("/dev_admin");
                }}
              >
                <ListItemIcon>
                  <Explore fontSize="small" color="action" />
                </ListItemIcon>
                <ListItemText>Developer Area</ListItemText>
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                handleUserMenuClose();
                logout();
              }}
            >
              <ListItemIcon>
                <ExitToApp fontSize="small" color="action" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default CustomAppBar;
